import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Co2Outlined, InfoOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, Link } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import {
  formatNumber,
  LinearProgressChart,
  LinearProgressList,
  LinearProgressListItem,
  LinearProgressListSubheader,
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isNil, isEmpty } from 'ramda';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { useDateRange } from '../../components/DateRangeProvider';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { TooltipTypography } from '~/components/TooltipTypography/TooltipTypography';
import { VirtualList, VirtualListProps } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';
import { convertDateRangeToTimeDimension } from '~/util/cube';

const MAX_ITEMS = 5;

const tooltipFormatter = formatNumber(NumberFormatPreset.INTEGER);

interface RenderItem {
  id: string;
  value: number;
  totalValue: number;
}

export const GeneralEmissionCommodityWidget = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'GeneralEmissionCommodityWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'general-emission-commodity' });
  const [dialogProps, dialogOpen] = useOpenClose();

  const [parentCommodityGroup /* setParentCommodityGroup */] = useState<string>();
  const cube = parentCommodityGroup ? 'EmissionsByCommodityGroupChildren' : 'EmissionsByCommodityGroupRoot';
  const { currentDateRange } = useDateRange();

  const filters: Filter[] = [
    {
      member: `${cube}.countryCode`,
      operator: 'equals',
      values: ['DE'], // Emissions are only available for EU states
    },
  ];
  const request: DeeplyReadonly<Query> = {
    dimensions: [`${cube}.commodityGroupId`, `${cube}.emissionValue`],
    timeDimensions: convertDateRangeToTimeDimension(`${cube}.date`, currentDateRange, 'year'),
    order: {
      [`${cube}.emissionValue`]: 'desc',
    },
    filters,
  };
  const requestTotals: DeeplyReadonly<Query> = {
    measures: [`${cube}.totalEmissionValue`, `${cube}.totalCommodityGroups`],
    timeDimensions: convertDateRangeToTimeDimension(`${cube}.date`, currentDateRange),

    filters,
  };

  const query = useCubeQuery({ ...request, limit: MAX_ITEMS });
  const queryTotals = useCubeQuery(requestTotals);

  const nodesTotal = queryTotals.resultSet?.rawData()?.map((segment) => ({
    totalValue: Number(segment[`${cube}.totalEmissionValue`]) || 0,
    totalCommodityGroups: Number(segment[`${cube}.totalCommodityGroups`]) || 0,
  }));

  const totalValue = nodesTotal?.[0]?.totalValue ?? 0;
  const totalCommodityGroups = nodesTotal?.[0]?.totalCommodityGroups ?? 0;

  const nodes = query.resultSet?.rawData()?.map((segment) => ({
    id: segment[`${cube}.commodityGroupId`],
    value: Number(segment[`${cube}.emissionValue`]) || 0,
    totalValue,
    totalCommodityGroups,
  }));

  const isNodesEmpty = isEmpty(nodes) || isNil(nodes);
  const { isLoading: isLoadingTotals } = queryTotals;
  const { isLoading: isLoadingNodes } = query;
  const isLoading = isLoadingTotals || isLoadingNodes;

  const hasItems = !!totalCommodityGroups;
  const hasMore = totalCommodityGroups > MAX_ITEMS;

  const renderItem: NonNullable<VirtualListProps<any>['renderItem']> = useMemo(
    () =>
      ({ item }: { item: RenderItem }) => {
        const { id, value, totalValue } = item;
        const barValue = value;
        const barMax = totalValue;
        return (
          <Link key={id} component={RouterLink} to={RouteId.commodityGroupDetail(id)} underline="none">
            <Box
              style={{ listStyleType: 'none' }}
              sx={{
                py: 4,
              }}
            >
              <LinearProgressListItem
                key={id}
                component="div"
                primary={<CuriosityValue ns={CuriosityNamespaceType.COMMODITY_GROUP} i18nKey={id} />}
                primaryRight={t(!totalValue ? 'value' : 'valueTotal', {
                  value,
                  totalValue,
                })}
              >
                {barValue !== null && barMax !== null && (
                  <LinearProgressChart value={barValue} max={barMax} tooltipFormatter={tooltipFormatter} />
                )}
              </LinearProgressListItem>
            </Box>
          </Link>
        );
      },
    [t],
  );

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="GeneralEmissionCommodityWidget">
        <WidgetHeader
          title={t('title')}
          subTitle={t('subTitle', currentDateRange as any) as string}
          icon={<WidgetIcon icon={<Co2Outlined fontSize="small" />} />}
          actionsRight={
            <TooltipTypography arrow title={t('info.tooltip')}>
              <InfoOutlined sx={{ color: 'text.secondary' }} />
            </TooltipTypography>
          }
        />
        <WidgetContent>
          {!isNodesEmpty ? (
            <LinearProgressList
              items={nodes}
              renderItem={renderItem}
              subheader={
                hasItems ? (
                  <LinearProgressListSubheader disableGutters disableSticky>
                    {t('listSubheader')}
                  </LinearProgressListSubheader>
                ) : undefined
              }
            />
          ) : isLoading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore || isNodesEmpty} onClick={dialogOpen}>
            {t('showAll', { count: totalCommodityGroups })}
          </Button>
        </WidgetFooter>
      </Widget>
      <Dialog
        {...dialogProps}
        sx={{
          '& .MuiDialog-paper': {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent>
          <Widget>
            <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<Co2Outlined fontSize="small" />} />} />
            <WidgetContent>
              <Box
                sx={{
                  marginBottom: 16,
                }}
              >
                <VirtualList
                  itemSize={88}
                  query={request}
                  queryOptions={{
                    skip: !dialogProps.open,
                    segmentMapper: (segment) => ({
                      id: segment[`${cube}.commodityGroupId`],
                      value: Number(segment[`${cube}.emissionValue`]) || 0,
                      totalValue,
                    }),
                  }}
                  renderItem={renderItem}
                />
              </Box>
            </WidgetContent>
          </Widget>
        </DialogContent>
      </Dialog>
    </>
  );
};
